import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { AdminService } from "src/rest/adminService";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "src/store/store";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import {
  STATUS_APPROVED,
  STATUS_REJECTED
} from "src/common/constants/statusConstants";

const debounce = (fn: (...args: any[]) => void, delay = 300) => {
  let t: any;
  return (...args: any[]) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

export const AcceptedUsers: React.FC<{ status?: typeof STATUS_APPROVED | typeof STATUS_REJECTED }> = ({
  status = STATUS_APPROVED,
}) => {
  const [users, setUsers] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
    const { globalLanguage } = globalInfo;
    const isHindiLabels = globalLanguage === 'HI';

  useEffect(() => {
    (async () => {
      try {
        const histStatus = status === STATUS_APPROVED ? STATUS_APPROVED : STATUS_REJECTED;
        const historyRes = await AdminService.getUserActionHistory(
          histStatus as any
        );
        const actions = Array.isArray(historyRes.data)
          ? historyRes.data
          : [historyRes.data];
        const list = actions.map((a: any) => ({
          userEmail: String(a?.userEmail || ""),
          action: String(a?.action || ""),
          role: String(a?.role || a?.userRole || ""),
          reason: a?.reason ? String(a.reason) : "",
          timestamp: a?.timeStamp ? String(a.timeStamp) : (a?.timestamp ? String(a.timestamp) : ""),
        }));
        setUsers(list);
        setFiltered(list);
      } catch (e) {
        console.error(`${status} users fetch error`, e);
        setUsers([]);
        setFiltered([]);
      }
    })();
  }, [status]);

  useEffect(() => {
    setPage(1);
  }, [filtered]);

  const runFilter = useMemo(
    () =>
      debounce((q: string) => {
        const lower = q.toLowerCase();
        setFiltered(
          users.filter((u) => (u.userEmail || "").toLowerCase().includes(lower))
        );
      }, 300),
    [users]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    runFilter(q);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const display = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Box>
      <InputGroup mb={4}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder = {isHindiLabels? "ईमेल से खोजें": "Search by email"}
          value={query}
          onChange={onChange}
        />
      </InputGroup>

      <TableContainer border="1px" borderColor="#EBEBEB" mt={3}>
        <Table variant="simple">
          <Thead backgroundColor="#F4F4F4">
            <Tr>
              <Th
                paddingLeft="12px"
                paddingRight="12px"
                paddingTop="20px"
                paddingBottom="20px"
              >
                {isHindiLabels ? Admin_Labels_Hindi.EMAIL:Admin_Labels_Eng.EMAIL}
              </Th>
              <Th
                paddingLeft="12px"
                paddingRight="12px"
                paddingTop="20px"
                paddingBottom="20px"
              >
                {isHindiLabels ? Admin_Labels_Hindi.STATUS:Admin_Labels_Eng.STATUS}
              </Th>
              <Th
                paddingLeft="12px"
                paddingRight="12px"
                paddingTop="20px"
                paddingBottom="20px"
              >
                Reason
              </Th>
              <Th
                paddingLeft="12px"
                paddingRight="12px"
                paddingTop="20px"
                paddingBottom="20px"
              >
                Timestamp
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {display.map((u, i) => (
              <Tr
                key={i}
                cursor="pointer"
                _hover={{ bg: "#f7f2ef" }}
                borderBottom="1px solid #EFEFEF"
              >
                <Td
                  paddingLeft="12px"
                  paddingRight="12px"
                  paddingTop="20px"
                  paddingBottom="20px"
                >
                  <RouterLink
                    to={`/admin/user/${encodeURIComponent(u.userEmail)}`}
                    state={{ fromApp: true, backTo: status }}
                  >
                    {u.userEmail}
                  </RouterLink>
                </Td>
                <Td
                  paddingLeft="12px"
                  paddingRight="12px"
                  paddingTop="20px"
                  paddingBottom="20px"
                >
                  {u.action}
                </Td>
                <Td
                  paddingLeft="12px"
                  paddingRight="12px"
                  paddingTop="20px"
                  paddingBottom="20px"
                >
                  {u.reason ? (
                    (() => {
                      const reason = u.reason as string;
                      const showTip = reason.length > 50;
                      const content = (
                        <Text
                          noOfLines={1}
                          isTruncated
                          maxW={{ base: "140px", md: "240px", lg: "320px" }}
                        >
                          {reason}
                        </Text>
                      );
                      return showTip ? (
                        <Tooltip label={reason} hasArrow placement="top">
                          {content}
                        </Tooltip>
                      ) : (
                        content
                      );
                    })()
                  ) : (
                    "—"
                  )}
                </Td>
                <Td
                  paddingLeft="12px"
                  paddingRight="12px"
                  paddingTop="20px"
                  paddingBottom="20px"
                >
                  {u.timestamp ? new Date(u.timestamp).toLocaleString() : "—"}
                </Td>
              </Tr>
            ))}
            {display.length === 0 && (
              <Tr>
                <Td
                  colSpan={4}
                  paddingLeft="12px"
                  paddingRight="12px"
                  paddingTop="20px"
                  paddingBottom="20px"
                >
                  <Text textAlign="center" color="gray.500">
                    No results
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {filtered.length > pageSize && (
        <Box
          mt={3}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
            }}
          >
            Prev
          </button>
          <Text fontSize="sm">
            Page {page} of {totalPages}
          </Text>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
            }}
          >
            Next
          </button>
        </Box>
      )}
    </Box>
  );
};

export default AcceptedUsers;
